import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/** The Leadership Hub (GET /schools/mine/hub, backend services/leadershipHub.ts). */
export type HubPeriod = 'term' | '4w' | 'year' | 'custom';
/** Who sees scores (backend services/scoreMode.ts). */
export type ScoreMode = 'full' | 'team' | 'trends';
export type Direction = 'improving' | 'stable' | 'dipping';

export interface HubArea {
  id: string;
  name: string;
  teachers: number;
  lessons: number;
  /** Null when fewer than minGroup teachers are behind it, or with trends only. */
  average: number | null;
  previous: number | null;
  /** Against the previous period; null without enough teachers in both. */
  direction: Direction | null;
  /** How many indicator scores landed on 1, 2, 3, 4 and 5. */
  distribution: number[] | null;
  notSeenRate: number | null;
  indicators: { id: string; name: string; teachers: number; average: number | null; direction: Direction | null; notSeenRate: number | null }[];
}

export interface Hub {
  school: { name: string; scoreMode: ScoreMode; canChangeScoreMode: boolean };
  minGroup: number;
  period: {
    key: HubPeriod;
    label: string;
    from: string;
    to: string;
    previousLabel: string;
    /** The organisation set its own term dates. */
    customTerms?: boolean;
    /** Terms to choose from, newest first (`to` is the last day). Missing from older APIs. */
    terms?: { label: string; from: string; to: string }[];
  };
  filters: { subject: string | null; age: string | null; subjects: string[]; ages: string[] };
  frameworks: { id: string; name: string; lessons: number }[];
  framework: { id: string; name: string; version: number; versionFrom: string | null; earlierVersions: boolean } | null;
  adoption: {
    staff: number;
    activeThisWeek: number;
    activeThisMonth: number;
    lessons: number;
    lessonsBefore: number;
    weekly: { week: string; lessons: number; teachers: number }[];
    quiet: { id: string; name: string; lastLessonAt: string | null; lastNudgedAt: string | null }[];
  };
  loop: {
    withFocus: number;
    lessons: number;
    lessonsWithFocus: number;
    reflections: number;
    tried: { yes: number; partly: number; notYet: number };
    focusMeasured: number;
    focusImproving: number;
  };
  scoredTeachers: number;
  /** A subject or age filter left fewer than minGroup teachers on one side, so scores are held back. */
  filterWithheld?: boolean;
  /** The organisation's priority areas, tracked through the framework indicators that match them. */
  priorities: { text: string; indicators: { id: string; name: string }[]; average: number | null; previous: number | null; direction: Direction | null; teachers: number }[];
  areas: HubArea[];
  strengths: { id: string; name: string; average: number | null }[];
  nextSteps: { id: string; name: string; average: number | null; example: string | null }[];
  checkpoints: { label: string; from: string; version: number; areas: { id: string; average: number | null; direction: Direction | null; teachers: number }[] }[];
  /** Only with full scores. */
  people:
    | {
        id: string;
        name: string;
        lessons: number;
        average: number | null;
        latest: number | null;
        trend: Direction | null;
        focus: string | null;
        lastLessonAt: string | null;
      }[]
    | null;
}

/** `at` is any day in the term to show (period 'term'); `from`/`to` (YYYY-MM-DD) for 'custom'. */
export type HubQuery = { period: HubPeriod; at?: string; from?: string; to?: string; frameworkId?: string; subject?: string; age?: string };

/** The query string for the Hub and its downloads (GET /schools/mine/hub, hub.csv, hub.pdf). */
export function hubParams(q: HubQuery): string {
  const params = new URLSearchParams({ period: q.period });
  if (q.period === 'term' && q.at) params.set('at', q.at);
  if (q.period === 'custom' && q.from && q.to) {
    params.set('from', q.from);
    params.set('to', q.to);
  }
  if (q.frameworkId) params.set('frameworkId', q.frameworkId);
  if (q.subject) params.set('subject', q.subject);
  if (q.age) params.set('age', q.age);
  return params.toString();
}

export function useHub(q: HubQuery) {
  return useQuery({
    queryKey: ['hub', q],
    queryFn: () => api<Hub>(`/schools/mine/hub?${hubParams(q)}`),
    placeholderData: (prev) => prev,
  });
}

export interface Wellbeing {
  thisWeek: { avgMood: number | null; avgEnergy: number | null; totalCheckins: number; teachersWithCheckins: number; teacherCount: number };
  lastWeek: { avgMood: number | null; avgEnergy: number | null };
  trends: { weekly: { label: string; avgMood: number | null; avgEnergy: number | null; count: number }[] };
}

export function useWellbeing() {
  return useQuery({ queryKey: ['hub-wellbeing'], queryFn: () => api<Wellbeing>('/schools/mine/wellbeing-summary') });
}

/** Owners: how the organisation uses scores. Everyone else is told. */
export function useScoreMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: ScoreMode) => api<{ scoreMode: ScoreMode }>('/schools/mine/score-mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['hub'] }), qc.invalidateQueries({ queryKey: ['school'] })]),
  });
}
